import React, { useEffect } from 'react';
import {
  Button,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  styled
} from '@mui/material';
import { dateFormatter, getReccurssiveObjectKeys } from '../../utils';
import { CheckBox, DeleteOutline } from '@mui/icons-material';
import SendIcon from '@mui/icons-material/Send';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import EditIcon from '@mui/icons-material/Edit';
import { Checkbox } from '../Input';
import PrintIcon from '@mui/icons-material/Print';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(0.9),
  whiteSpace: 'nowrap',
  fontSize: '15px',
  fontWeight: 500
}));

export const Table = ({
  data,
  columns,
  onRowClick,
  containerSX,
  sx,
  size,
  showDeleteAction,
  showAuthorize,
  onAuthorizeHandler,
  onDeleteHandler,
  showInputField,
  handleInputChange,
  inputValues,
  onSumChange,
  showStatuses,
  handleStatusDefChange,
  handleStatusCancelChange,
  handleStatusDoneChange,
  editMedicine,
  onClickEdit,
  showCollected,
  handleRowSelect,
  isSelected,
  roomCleaned,
  handleRoomCleaned,
  showOtStatus,
  showEditButton,
  handleDeleteRow,
  onClickEditRow,
  showOk,
  showDoc,
  selectedRows,
  Approved,
  printSalary,
  handlePrintSlip
}) => {
  //function for the form retail sale return
  useEffect(() => {
    if (typeof onSumChange !== 'function') {
      return;
    }
    const totalAmount = data.reduce((sum, row) => {
      const returnQty = parseFloat(inputValues[row._id]?.['Return_Qty'] || 0);
      const rate = parseFloat(row.rate || 0);
      return sum + returnQty * rate;
    }, 0);

    onSumChange(totalAmount);
  }, [data, inputValues, onSumChange]);

  return (
    <TableContainer sx={{ maxHeight: '250px', ...containerSX }}>
      <MuiTable sx={sx} size={size || 'medium'}>
        <TableHead>
          <TableRow sx={{ backgroundColor: 'lightblue' }}>
            <StyledTableCell key="sr.no.-cell" sx={{ fontWeight: 'bold' }}>
              Sr. No.
            </StyledTableCell>
            {columns.map((column, index) => (
              <StyledTableCell sx={{ fontWeight: 'bold' }} key={`header-cell-${index}`}>
                {column.header}
              </StyledTableCell>
            ))}
            {showDeleteAction && (
              <StyledTableCell key="action-cell" sx={{ fontWeight: 'bold' }}>
                Action
              </StyledTableCell>
            )}
            {showAuthorize && (
              <StyledTableCell key="authorize-cell" sx={{ fontWeight: 'bold' }}>
                Authorize
              </StyledTableCell>
            )}
            {showCollected && (
              <StyledTableCell key="collected-cell" sx={{ fontWeight: 'bold' }}>
                Sample Status
              </StyledTableCell>
            )}
            {showStatuses && (
              <StyledTableCell key="statuses-cell" sx={{ fontWeight: 'bold' }}>
                Statuses
              </StyledTableCell>
            )}
            {editMedicine && (
              <StyledTableCell key="edit-cell" sx={{ fontWeight: 'bold' }}>
                Edit
              </StyledTableCell>
            )}
            {roomCleaned && (
              <StyledTableCell key="room-cleaned-status-cell" sx={{ fontWeight: 'bold' }}>
                Room Cleaned
              </StyledTableCell>
            )}
            {showOtStatus && (
              <StyledTableCell key="show-ot-status-cell" sx={{ fontWeight: 'bold' }}>
                Ot Schedule Status
              </StyledTableCell>
            )}
            {showEditButton && (
              <StyledTableCell key="edit-button-cell" sx={{ fontWeight: 'bold' }}>
                Edit
              </StyledTableCell>
            )}

            {showOk && (
              <>
                <StyledTableCell key="ok-cell" sx={{ fontWeight: 'bold' }}>
                  Ok
                </StyledTableCell>
              </>
            )}
            {showDoc && (
              <>
                <StyledTableCell key="doc-cell" sx={{ fontWeight: 'bold' }}>
                  Doc
                </StyledTableCell>
              </>
            )}
            {Approved && (
              <>
                <StyledTableCell key="Approved-cell" sx={{ fontWeight: 'bold' }}>
                  Approve
                </StyledTableCell>
              </>
            )}
            {printSalary && (
              <>
                <StyledTableCell key="printSalary-cell" sx={{ fontWeight: 'bold' }}>
                Salary Slip
                </StyledTableCell>
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, rowIndex) => (
            <TableRow
              {...(onRowClick && {
                onClick: () => {
                  onRowClick(row);
                }
              })}
              sx={{
                backgroundColor: rowIndex % 2 ? 'grey.200' : 'none',
                cursor: onRowClick ? 'pointer' : 'default',
                '&:hover': {
                  backgroundColor: 'grey.300'
                },
                transition: 'all 0.1s ease-in'
              }}
              key={row._id}
            >
              <StyledTableCell key={`${row._id}-cell0`}>{rowIndex + 1}</StyledTableCell>
              {columns.map(({ header, accessor, sx }, colIndex) => (
                <StyledTableCell sx={sx} key={`${row._id}-cell${colIndex + 1}`}>
                  {header === 'Result' && showInputField ? (
                    <TextField
                      value={inputValues[row._id]?.[header] || ''}
                      onChange={(e) => handleInputChange(row._id, header, e.target.value)}
                      variant="outlined"
                      size="small"
                      sx={{
                        width: '60px',
                        '& .MuiOutlinedInput-input': {
                          padding: '4px 8px' // Adjust the vertical and horizontal padding as needed
                        },
                        '& .MuiInputBase-root': {
                          padding: '0px' // Ensure there's no extra padding around the input box
                        }
                      }}
                    />
                  ) : header === 'Return_Qty' && showInputField ? (
                    <TextField
                      value={inputValues[row._id]?.[header] || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        handleInputChange(row._id, header, value);
                      }}
                      variant="outlined"
                      size="small"
                      sx={{ width: '60px' }}
                      InputProps={{
                        style: {
                          fontSize: '12px', // Increase the font size to your desired value
                          fontWeight: 700
                        }
                      }}
                    />
                  ) : header === 'Remarks' && showInputField ? (
                    <TextField
                      value={selectedRows.find((selectedRow) => selectedRow._id === row._id)?.Remarks || ''}
                      onChange={(e) => handleInputChange(row._id, 'Remarks', e.target.value)}
                      variant="outlined"
                      size="small"
                      sx={{
                        width: '120px',
                        '& .MuiOutlinedInput-input': {
                          padding: '4px 8px'
                        },
                        '& .MuiInputBase-root': {
                          padding: '0px'
                        }
                      }}
                    />
                  ) : header === 'Amounts' ? (
                    (parseFloat(inputValues[row._id]?.['Return_Qty'] || 0) * parseFloat(row.rate || 0)).toFixed(2) ||
                    '0.00'
                  ) : typeof accessor === 'function' ? (
                    accessor(row) || '-'
                  ) : (
                    <>
                      {header === 'Created At' || header === 'Date'
                        ? dateFormatter(getReccurssiveObjectKeys(row, accessor)) || '-'
                        : getReccurssiveObjectKeys(row, accessor) || '-'}
                    </>
                  )}
                </StyledTableCell>
              ))}

              {showDeleteAction && (
                <StyledTableCell key={`${row._id}-cell-action`}>
                  <Button color="error" onClick={() => onDeleteHandler(row._id)} sx={{ padding: '2px' }}>
                    <CancelIcon sx={{ fontSize: 20 }} />
                  </Button>
                </StyledTableCell>
              )}
              {showAuthorize && (
                <StyledTableCell key={`${row._id}-cell-authorize`}>
                  <Button color="success" size="24" onClick={() => onAuthorizeHandler(row)}>
                    <SendIcon />
                  </Button>
                </StyledTableCell>
              )}
              {showStatuses && (
                <StyledTableCell key={`${row._id}-cell-statuses`}>
                  <HelpCenterIcon
                    onClick={() => handleStatusDefChange(row, 'deferred')}
                    type="submit"
                    sx={{ fontSize: 24, color: 'black' }}
                  />
                  <CancelIcon
                    onClick={() => handleStatusCancelChange(row, 'cancel')}
                    type="submit"
                    sx={{ fontSize: 24, color: 'red' }}
                  />
                  <CheckBoxIcon
                    onClick={() => handleStatusDoneChange(row, 'done')}
                    type="submit"
                    sx={{ fontSize: 24, color: 'green' }}
                  />
                </StyledTableCell>
              )}
              {editMedicine && (
                <StyledTableCell key={`${row._id}-edit-cell`}>
                  <EditIcon onClick={() => onClickEdit(row)} type="submit" />
                </StyledTableCell>
              )}
              {showCollected && (
                <StyledTableCell key={`-collected-cell`}>
                  <Checkbox
                    checked={isSelected(row)}
                    onChange={() => handleRowSelect(row)}
                    color="primary"
                    name={`checkbox-${row._id}`}
                    fontSize={18}
                  />
                </StyledTableCell>
              )}
              {Approved && (
                <StyledTableCell key={`-Approved-cell`}>
                  <Checkbox
                    checked={isSelected(row)}
                    onChange={() => handleRowSelect(row)}
                    color="primary"
                    name={`checkbox-${row._id}`}
                    fontSize={18}
                  />
                </StyledTableCell>
              )}
              {roomCleaned && (
                <StyledTableCell key={`${row._id}-room-cleaned-status-cell`}>
                  <Button color="success" variant="contained" size="24" onClick={() => handleRoomCleaned(row)}>
                    Clean Room
                  </Button>
                </StyledTableCell>
              )}
              {printSalary && (
                <StyledTableCell key={`${row._id}-printSalary-cell`}>
                  <Button color="primary" variant="contained" size="24" onClick={() => handlePrintSlip(row)}>
                 <PrintIcon />
                  </Button>
                </StyledTableCell>
              )}
              {showOtStatus && (
                <StyledTableCell key={`${row._id}-show-ot-status-cell`}>
                  <CancelIcon
                    onClick={() => handleStatusCancelChange(row, 'cancel')}
                    type="submit"
                    sx={{ fontSize: 24, color: 'red' }}
                  />
                  <CheckBoxIcon
                    onClick={() => handleStatusDoneChange(row, 'done')}
                    type="submit"
                    sx={{ fontSize: 24, color: 'green' }}
                  />
                </StyledTableCell>
              )}
              {showEditButton && (
                <StyledTableCell key={`${row._id}-edit-button-cell`}>
                  <EditIcon onClick={() => onClickEditRow(row)} type="submit" sx={{ fontSize: 20, color: 'green' }} />
                  <CancelIcon
                    onClick={() => handleDeleteRow(row._id)}
                    type="submit"
                    sx={{ fontSize: 24, color: 'red' }}
                  />
                </StyledTableCell>
              )}
              {showOk && (
                <StyledTableCell>
                  <Checkbox
                    sx={{
                      transform: 'scale(0.8)', // Adjust the scale to make it smaller
                      padding: '0' // Reduce padding to fit better
                    }}
                    name={row?._id}
                    checked={isSelected(row, 'ok')}
                    onChange={() => handleRowSelect(row, 'ok')}
                  />
                </StyledTableCell>
              )}
              {showDoc && (
                <StyledTableCell>
                  <Checkbox
                    sx={{
                      transform: 'scale(0.8)', // Adjust the scale to make it smaller
                      padding: '0' // Reduce padding to fit better
                    }}
                    name={row?._id}
                    checked={isSelected(row, 'doc')}
                    onChange={() => handleRowSelect(row, 'doc')}
                  />
                </StyledTableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </MuiTable>
    </TableContainer>
  );
};
